import React from 'react';

import DownloadIcon from 'icons/ic-download.svg';
import PersonIcon from 'icons/person.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import RowBar from 'components/application/RowBar';
import SearchField from 'components/application/SearchField';
import SegmentedControls from 'components/application/SegmentedControls';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

export default function PersonnelBar({
  isActionButtonVisible,
  isArchived,
  isDomainPersonnelLoading,
  onArchiveToggle,
  onNewPersonnel,
  onSearchInputChange,
  onSearchReset,
  onTrainingMatrixButtonClick,
  personnelSearch,
}) {
  const currentActor = useCurrentActor();

  const { hasArchivedPersonnelAccess } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelBar',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleSegmentItemClick = (selection) => {
    if (selection !== activeToggleChoice) onArchiveToggle();
  };

  const activeToggleChoice =
    isArchived ? i18nTCommon('shared.archived') : i18nTCommon('shared.current');

  if (isDomainPersonnelLoading) {
    return <RowBar content={<TextSkeleton />} />;
  }

  return (
    <RowBar
      actions={
        <>
          {currentActor.isAllowedFeature('training_register') &&
            isActionButtonVisible && (
              <div className='tooltip-parent tw-mr-3'>
                <OutlinedButton
                  color='grey'
                  onClick={onTrainingMatrixButtonClick}
                  size='sm'
                >
                  <DownloadIcon className='-tw-ml-2' height={24} width={24} />
                  <span>{i18nTCommon('training.trainingMatrix')}</span>
                </OutlinedButton>
              </div>
            )}
          {isActionButtonVisible && (
            <OutlinedButton color='grey' onClick={onNewPersonnel} size='sm'>
              <PersonIcon className='-tw-ml-2' height={24} width={24} />
              <span>{i18nTCommon('personnel.addPersonnel')}</span>
            </OutlinedButton>
          )}
        </>
      }
      content={
        <span className='separated-items tw-flex tw-items-center'>
          {hasArchivedPersonnelAccess && (
            <SegmentedControls
              activeChoice={activeToggleChoice}
              choices={[
                i18nTCommon('shared.current'),
                i18nTCommon('shared.archived'),
              ]}
              onItemClick={handleSegmentItemClick}
            />
          )}
          <SearchField
            name='personnelSearch'
            onChange={onSearchInputChange}
            onReset={onSearchReset}
            placeholder={i18nT(
              `search.placeholder.${isArchived ? 'archived' : 'current'}`,
            )}
            value={personnelSearch}
          />
        </span>
      }
      modifiers={[
        'border-top-curved',
        'border-bottom-none',
        'flex-align-items-ie-fix',
      ]}
    />
  );
}

PersonnelBar.propTypes = {
  isActionButtonVisible: PropTypes.bool,
  isArchived: PropTypes.bool,
  isDomainPersonnelLoading: PropTypes.bool,
  onArchiveToggle: PropTypes.func,
  onTrainingMatrixButtonClick: isTrainingMatrixButtonClickRequired,
  onNewPersonnel: PropTypes.func.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onSearchReset: PropTypes.func.isRequired,
  personnelSearch: PropTypes.string,
};

function isTrainingMatrixButtonClickRequired(props, propName, componentName) {
  if (props.isActionButtonVisible && !props.onTrainingMatrixButtonClick) {
    return new Error(
      `The prop \`${propName}\` is marked as required in \`${componentName}\`, but its value is \`undefined\`.`,
    );
  }
}
